import { useState, useEffect } from "react";
import { X, Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart, startCheckout, fetchPaymentMethods } from "@/context/CartContext";
import { useCheckoutQuote } from "@/hooks/useCheckoutQuote";
import { PromoInput, CartSummary } from "@/components/CartTotals";

function PayPalLogo() {
  return (
    <span className="font-bold italic tracking-tight text-base leading-none">
      <span className="text-[#003087]">Pay</span><span className="text-[#009cde]">Pal</span>
    </span>
  );
}

function CartLine({ item, updateQty, removeItem }) {
  return (
    <div className="flex gap-3 items-center bg-white rounded-xl border border-gold/10 p-3" data-testid={`cart-item-${item.lookupKey}`}>
      <img src={item.image} alt={item.name} className="w-14 h-14 object-contain rounded-lg bg-cream flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-brown-dark truncate">{item.name}</p>
        <p className="text-xs text-gold font-bold">${item.price.toFixed(2)}</p>
        <div className="flex items-center gap-2 mt-1.5">
          <button onClick={() => updateQty(item.lookupKey, -1)} className="w-6 h-6 rounded-full border border-gold/25 flex items-center justify-center text-gray-500 hover:text-gold" data-testid={`cart-qty-dec-${item.lookupKey}`}><Minus size={12} /></button>
          <span className="text-sm font-semibold text-brown-dark w-5 text-center" data-testid={`cart-qty-${item.lookupKey}`}>{item.qty}</span>
          <button onClick={() => updateQty(item.lookupKey, 1)} className="w-6 h-6 rounded-full border border-gold/25 flex items-center justify-center text-gray-500 hover:text-gold" data-testid={`cart-qty-inc-${item.lookupKey}`}><Plus size={12} /></button>
        </div>
      </div>
      <button onClick={() => removeItem(item.lookupKey)} className="text-gray-300 hover:text-red-400 transition-colors" data-testid={`cart-remove-${item.lookupKey}`} aria-label="Remove">
        <Trash2 size={16} />
      </button>
    </div>
  );
}

export default function CartDrawer() {
  const { items, updateQty, removeItem, drawerOpen, setDrawerOpen } = useCart();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [promo, setPromo] = useState("");
  const [methods, setMethods] = useState({ paypal: false, discount_code: "GOLD10", discount_pct: 10, paypal_tax_rate: 0, shipping_flat: 7, free_shipping_over: 65 });
  const { quote, pending, error: quoteError } = useCheckoutQuote(items, promo, drawerOpen);

  useEffect(() => {
    if (!items.length) setPromo("");
    setError("");
  }, [items, promo]);

  useEffect(() => {
    if (drawerOpen) fetchPaymentMethods().then((m) => setMethods((prev) => ({ ...prev, ...m })));
  }, [drawerOpen]);

  if (!drawerOpen) return null;

  const checkout = async () => {
    setLoading(true);
    setError("");
    try {
      const code = quote?.promo_code;
      await startCheckout(items.map((i) => ({ lookup_key: i.lookupKey, quantity: i.qty })), code);
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="cart-drawer-overlay">
      <button aria-label="Close cart backdrop" className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} data-testid="cart-backdrop-close" />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-cream-light shadow-2xl flex flex-col overflow-y-auto" role="dialog" aria-modal="true" aria-label="Your cart" data-testid="cart-drawer">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gold/10">
          <h3 className="font-playfair text-xl font-bold text-brown-dark flex items-center gap-2">
            <ShoppingBag size={20} className="text-gold" /> Your Cart
          </h3>
          <button onClick={() => setDrawerOpen(false)} className="text-gray-400 hover:text-brown-dark" data-testid="cart-close-btn" aria-label="Close cart"><X size={22} /></button>
        </div>
        <div className="flex-1 min-h-24 overflow-y-auto px-6 py-5 space-y-3">
          {items.length === 0 ? (
            <p className="text-gray-400 text-sm text-center mt-10" data-testid="cart-empty-message">Your cart is empty. Add a weekly 7-pack to get started.</p>
          ) : (
            items.map((item) => <CartLine key={item.lookupKey} item={item} updateQty={updateQty} removeItem={removeItem} />)
          )}
        </div>
        {items.length > 0 && (
          <div className="px-6 py-5 border-t border-gold/10 bg-white shrink-0">
            <PromoInput promo={promo} setPromo={setPromo} quote={quote} pending={pending} />
            <CartSummary quote={quote} pending={pending} />
            {quoteError && <p className="text-red-600 text-xs mb-2" role="alert" data-testid="cart-promo-error">{quoteError}</p>}
            {error && <p className="text-red-400 text-xs mb-2" data-testid="cart-error">{error}</p>}
            {methods.paypal ? (
              <button onClick={checkout} disabled={loading || pending || !quote || !!quoteError || methods.mode === "invalid"} className="w-full flex items-center justify-center gap-2 rounded-full bg-[#ffc439] hover:bg-[#f2ba36] transition-colors py-3 disabled:opacity-60" data-testid="cart-paypal-btn" aria-label="Pay with PayPal">
                {loading ? <span className="text-sm font-semibold text-[#003087]">Redirecting to PayPal...</span> : <><span className="text-sm font-semibold text-[#003087]">Checkout with</span><PayPalLogo /></>}
              </button>
            ) : (
              <p className="text-xs text-gray-400 text-center" data-testid="cart-checkout-unavailable">Online checkout is temporarily unavailable.</p>
            )}
            <p className="text-[11px] text-gray-400 text-center mt-3">PayPal accepts debit &amp; credit cards — no PayPal account needed.</p>
            {methods.mode === "sandbox" && (
              <p className="text-[11px] font-semibold text-red-500 text-center mt-2" data-testid="cart-sandbox-warning">Test mode — payments are not real.</p>
            )}
            {methods.mode === "invalid" && (
              <p className="text-[11px] font-semibold text-red-500 text-center mt-2" data-testid="cart-config-error">Checkout is temporarily unavailable — payment configuration error.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
